import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Settings, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemConfig() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data || {});
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Configuration updated successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Settings className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Settings className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">System Configuration</h2>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">General Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Platform Name
              </label>
              <input
                type="text"
                value={config.platform_name || 'RefrX'}
                onChange={(e) => updateConfig('platform_name', e.target.value)}
                className="w-full px-4 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Support Email
              </label>
              <input
                type="email"
                value={config.support_email || ''}
                onChange={(e) => updateConfig('support_email', e.target.value)}
                className="w-full px-4 py-2 border rounded-md"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Trust Score Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Minimum Trust Score for Verification
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.min_trust_score || 50}
                onChange={(e) => updateConfig('min_trust_score', parseFloat(e.target.value))}
                className="w-full px-4 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Document Verification Bonus Points
              </label>
              <input
                type="number"
                min="0"
                max="20"
                value={config.doc_verification_bonus || 5}
                onChange={(e) => updateConfig('doc_verification_bonus', parseFloat(e.target.value))}
                className="w-full px-4 py-2 border rounded-md"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Feature Flags</h3>
          <div className="space-y-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.enable_email_verification || false}
                onChange={(e) => updateConfig('enable_email_verification', e.target.checked)}
                className="w-4 h-4"
              />
              <span>Enable Email Verification</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.enable_google_auth || false}
                onChange={(e) => updateConfig('enable_google_auth', e.target.checked)}
                className="w-4 h-4"
              />
              <span>Enable Google Authentication</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.enable_recruiter_portal || false}
                onChange={(e) => updateConfig('enable_recruiter_portal', e.target.checked)}
                className="w-4 h-4"
              />
              <span>Enable Recruiter Portal</span>
            </label>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </form>
    </div>
  );
}
